<template>
  <v-sheet elevation="2" class="px-12 py-8 rounded white">
    <div class="mb-6">
      <p class="h6 pa-0 mb-0">
        {{ $t('common.category_other') }}
      </p>
      <p
        class="mt-2 mb-0 pa-0 grey--text text--darken-2"
        v-html="$t('site_settings.categories_subtitle')"
      />
    </div>
    <template v-for="category in categories">
      <v-hover v-slot:default="{ hover }" :key="category.id">
        <div
          v-if="shouldShowCategory(category)"
          class="d-flex items-start justify-space-between py-2 px-3 mb-3"
          :key="category.id"
          :class="{
            'grey--text': shouldDisableCategory(category),
            hover: hover,
            grey: hover,
            'lighten-5': hover,
          }"
        >
          <p class="ma-0 mt-1" data-testid="category-name">
            {{ category.text }}
          </p>
          <v-tooltip
            top
            :key="category.id"
            :disabled="!shouldDisableCategory(category)"
          >
            <template v-slot:activator="{ on }">
              <div v-on="on">
                <v-switch
                  class="ma-0 pa-0 d-flex items-center"
                  hide-details
                  :id="category.id"
                  :disabled="shouldDisableCategory(category)"
                  :input-value="categoryIsVisible(category)"
                  @click.native="setRule(category)"
                ></v-switch>
              </div>
            </template>
            {{ $t('site_settings.categories_tooltip') }}
          </v-tooltip>
        </div>
      </v-hover>
    </template>
  </v-sheet>
</template>
<script>
import mixin from '@/mixins/mixin.vue'
import { mapActions } from 'vuex'
import _ from 'lodash'

export default {
  name: 'Categories',
  mixins: [mixin],

  props: {
    siteId: {
      type: String,
      required: true,
    },
    organizationId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      categories: [],
      siteRules: [],
      organizationRules: [],
    }
  },

  async mounted() {
    const catalog = await this.getCategoriesCatalog()
    await this.getSiteRules()
    await this.getOrganizationRules()
    this.categories = this.formatCategories(catalog)
  },

  methods: {
    ...mapActions({
      fetchSiteRules: 'categories/getSiteRules',
      createRule: 'categories/createSiteRule',
      updateRule: 'categories/updateSiteRule',
      getCatalog: 'categories/getOrganizationCatalog',
      fetchOrganizationRules: 'categories/getOrganizationRules',
    }),

    async getCategoriesCatalog() {
      try {
        const response = await this.getCatalog({
          organizationId: this.organizationId,
        })

        return response
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async getSiteRules() {
      try {
        const response = await this.fetchSiteRules({
          organizationId: this.organizationId,
          siteId: this.siteId,
        })
        this.siteRules = response.data
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async getOrganizationRules() {
      try {
        const response = await this.fetchOrganizationRules({
          organizationId: this.organizationId,
        })
        this.organizationRules = response.data
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    formatCategories(catalog) {
      const categories = _.chain(catalog)
        .map(category => ({
          ...category,
          rule: this.getCategoryRule(category),
          loading: false,
          text: this.$t(`common.categories.${category.name}`),
        }))
        .orderBy('text', 'asc')
        .value()

      const other = _.remove(categories, c => c.name === 'other')
      categories.push(other[0])
      return categories
    },

    shouldDisableCategory(category) {
      const rule = this.organizationRules.find(
        rule => rule.category.id === category.id
      )
      return Boolean(category.loading || category.name === 'other' || rule)
    },

    shouldShowCategory(category) {
      const rule = this.organizationRules.find(
        rule => rule.category.id === category.id
      )
      return rule?.isVisible ?? true
    },

    getCategoryRule(category) {
      const organizationRule = this.organizationRules.find(
        rule => rule.category.id === category.id
      )

      if (organizationRule) return organizationRule

      const siteRule = this.siteRules.find(
        rule => rule.category.id === category.id
      )

      if (siteRule) return siteRule

      return { isVisible: category.isDefault }
    },

    setRule(category) {
      const payload = {
        organizationId: this.organizationId,
        siteId: this.siteId,
        rule: {
          isVisible: !category?.rule?.isVisible,
        },
      }
      if (category?.rule?.id) {
        payload.ruleId = category.rule.id
        return this.updateCategoryRule(payload, category)
      } else {
        payload.rule.category = category.id
        return this.createCategoryRule(payload, category)
      }
    },

    async updateCategoryRule(payload, category) {
      category.loading = true
      try {
        await this.updateRule(payload)
      } catch (err) {
        this.handleRequestError(err)
        category.rule.isVisible = !category.rule.isVisible
      } finally {
        category.loading = false
        this.getSiteRules()
      }
    },

    async createCategoryRule(payload, category) {
      category.loading = true
      try {
        await this.createRule(payload)
      } catch (err) {
        this.handleRequestError(err)
        category.rule.isVisible = !category.rule.isVisible
      } finally {
        category.loading = false
        this.getSiteRules()
      }
    },

    categoryIsVisible(category) {
      return category?.rule?.isVisible ?? false
    },
  },
}
</script>
<style lang="scss" scoped>
.hover {
  border-radius: 4px;
}
</style>
